// Settings → Security section: 2FA enable/disable + recovery codes.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Download, X, Check, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SecuritySection() {
  const [status, setStatus] = useState(null); // {enabled, setup_pending, recovery_codes_remaining, tenant_enforces_2fa}
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`);
      setStatus(data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  if (!status) return null;
  const { enabled, recovery_codes_remaining: remaining, tenant_enforces_2fa: enforced } = status;

  return (
    <div data-testid="settings-security-section" className="mb-8">
      <div className="mb-3" style={{
        fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "1.5px",
        color: "#9198b0", textTransform: "uppercase",
      }}>Security</div>

      <div className="rounded-md p-5" style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}>
        <div className="flex items-start gap-4">
          <div className="rounded-md flex items-center justify-center" style={{
            width: 38, height: 38,
            background: enabled ? "rgba(78,208,132,0.15)" : "rgba(243,177,74,0.12)",
            border: `1px solid ${enabled ? "rgba(78,208,132,0.45)" : "rgba(243,177,74,0.45)"}`,
            color: enabled ? "#4ed084" : "#f3b14a",
          }}>
            <Shield size={18} />
          </div>
          <div className="flex-1">
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
              Two-factor authentication (2FA)
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded"
                style={{
                  background: enabled ? "rgba(78,208,132,0.15)" : "rgba(145,152,176,0.15)",
                  color: enabled ? "#4ed084" : "#9198b0",
                  fontFamily: "'DM Mono', monospace", fontSize: 9,
                  letterSpacing: "1.2px", textTransform: "uppercase", fontWeight: 700,
                }}
                data-testid="security-2fa-status-badge"
                data-2fa-enabled={String(enabled)}
              >
                {enabled ? "✓ Enabled" : "○ Disabled"}
              </span>
              {enforced && (
                <span className="inline-flex items-center px-2 py-0.5 rounded" style={{
                  background: "rgba(226,75,74,0.15)", color: "#ff9a99",
                  fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "1px",
                }} data-testid="security-2fa-enforced-badge">
                  ⚠ REQUIRED BY TENANT
                </span>
              )}
              {enabled && (
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
                  {remaining} recovery codes left
                </span>
              )}
            </div>
            <div className="mt-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
              {enabled
                ? "Your account is protected by an authenticator app. You'll be asked for a 6-digit code every time you sign in."
                : "Add an extra layer of security with an authenticator app like Google Authenticator, 1Password, or Authy."}
            </div>
          </div>
          {!enabled && (
            <button
              onClick={() => setSetupOpen(true)}
              className="px-3 py-2 rounded-md transition-colors"
              style={{
                background: "#185FA5", border: "1px solid #185FA5", color: "#fff",
                fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700,
                fontSize: 12, letterSpacing: "0.4px", cursor: "pointer",
              }}
              data-testid="security-2fa-setup-btn"
            >
              Enable 2FA →
            </button>
          )}
          {enabled && (
            <button
              onClick={() => setDisableOpen(true)}
              className="px-3 py-2 rounded-md transition-colors"
              style={{
                background: "transparent", border: "1px solid rgba(226,75,74,0.45)",
                color: "#ff9a99",
                fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700,
                fontSize: 12, letterSpacing: "0.4px", cursor: "pointer",
              }}
              data-testid="security-2fa-disable-btn"
            >
              Disable
            </button>
          )}
        </div>
      </div>

      {setupOpen && <SetupModal onClose={() => { setSetupOpen(false); refresh(); }} />}
      {disableOpen && <DisableModal onClose={() => { setDisableOpen(false); refresh(); }} />}
    </div>
  );
}

const SetupModal = ({ onClose }) => {
  const [step, setStep] = useState("loading"); // loading | scan | verify | codes | error
  const [data, setData] = useState(null); // {secret, qr_png_base64}
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmedSaved, setConfirmedSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.post(`${API}/auth/2fa/setup`);
        setData(d);
        setStep("scan");
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to initialise 2FA setup.");
        setStep("error");
      }
    })();
  }, []);

  const verify = async () => {
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const { data: vs } = await axios.post(`${API}/auth/2fa/verify-setup`, { code });
      setCodes(vs.recovery_codes || []);
      setStep("codes");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Invalid code.");
    } finally {
      setBusy(false);
    }
  };

  const downloadCodes = () => {
    const txt = `SITROOM 2FA Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${codes.join("\n")}\n\nEach code can be used ONCE.`;
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "sitroom-2fa-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ModalShell onClose={onClose} title="Enable two-factor authentication" testid="security-2fa-setup-modal">
      {step === "loading" && <div className="text-center py-8" style={{ color: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>Generating secret…</div>}
      {step === "error" && (
        <div data-testid="security-2fa-setup-error">
          <div className="mb-4 px-3 py-2 rounded-md" style={{
            background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)",
            fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#E24B4A",
          }}>{err}</div>
          <button onClick={onClose} className="px-3 py-2 rounded-md" style={{
            background: "#185FA5", border: "1px solid #185FA5", color: "#fff",
            fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer",
          }}>Close</button>
        </div>
      )}
      {step === "scan" && data && (
        <div data-testid="security-2fa-scan-step">
          <div className="text-center mb-4">
            <img
              src={`data:image/png;base64,${data.qr_png_base64}`}
              alt="2FA QR code"
              className="mx-auto rounded"
              style={{ width: 200, height: 200, background: "#fff", padding: 6 }}
              data-testid="security-2fa-qr"
            />
          </div>
          <div className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
            1. Open Google Authenticator, 1Password, or Authy on your phone.<br />
            2. Scan this QR code to add SITROOM as a new account.<br />
            3. Enter the 6-digit code shown in the app below.
          </div>
          <details className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
            <summary style={{ cursor: "pointer", color: "#9ec3ec" }}>Can't scan? Enter the secret manually.</summary>
            <code className="block mt-2 px-2 py-1 rounded" style={{ background: "#0a0c12", color: "#cfd1d8", border: "1px solid rgba(255,255,255,0.08)", wordBreak: "break-all" }}>
              {data.secret}
            </code>
          </details>
          <input
            type="text" inputMode="numeric" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••"
            className="w-full px-3 py-3 rounded-md outline-none"
            style={{
              background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)",
              color: "#f0f2f8", fontFamily: "'DM Mono', monospace",
              fontSize: 22, letterSpacing: "8px", textAlign: "center",
            }}
            data-testid="security-2fa-verify-input"
            autoFocus
          />
          {err && <div className="mt-3 px-3 py-2 rounded-md" style={{
            background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)",
            fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#E24B4A",
          }} data-testid="security-2fa-verify-error">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={onClose} className="px-3 py-2 rounded-md" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#9198b0", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cancel</button>
            <button
              onClick={verify} disabled={busy || code.length !== 6}
              className="px-4 py-2 rounded-md"
              style={{
                background: busy || code.length !== 6 ? "#0c4076" : "#185FA5",
                border: "1px solid #185FA5", color: "#fff",
                fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12,
                cursor: busy || code.length !== 6 ? "not-allowed" : "pointer",
              }}
              data-testid="security-2fa-verify-submit"
            >{busy ? "Verifying…" : "Verify & enable"}</button>
          </div>
        </div>
      )}
      {step === "codes" && (
        <div data-testid="security-2fa-codes-step">
          <div className="mb-3 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f3b14a" }}>
            <AlertTriangle size={14} /> Save these recovery codes
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#c5cad8", lineHeight: 1.5, marginBottom: 12 }}>
            Each code can be used once if you lose access to your authenticator app. <span style={{ color: "#f3b14a" }}>This is the only time they will be shown.</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4" data-testid="security-2fa-codes-list">
            {codes.map((c, i) => (
              <code
                key={i}
                className="px-2 py-1.5 rounded text-center"
                style={{ background: "#0a0c12", border: "1px solid rgba(255,255,255,0.08)", color: "#cfd1d8", fontFamily: "'DM Mono', monospace", fontSize: 12, letterSpacing: "1px" }}
                data-testid="security-2fa-code"
              >{c}</code>
            ))}
          </div>
          <button onClick={downloadCodes} className="mb-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md" style={{
            background: "rgba(24,95,165,0.20)", border: "1px solid rgba(24,95,165,0.55)",
            color: "#9ec3ec", fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: "pointer",
          }} data-testid="security-2fa-codes-download">
            <Download size={12} /> Download as .txt
          </button>
          <label className="flex items-center gap-2 mb-4 cursor-pointer" data-testid="security-2fa-codes-saved-label">
            <input
              type="checkbox" checked={confirmedSaved}
              onChange={(e) => setConfirmedSaved(e.target.checked)}
              data-testid="security-2fa-codes-saved-check"
            />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cfd1d8" }}>
              I have saved these recovery codes in a safe place.
            </span>
          </label>
          <button
            onClick={onClose} disabled={!confirmedSaved}
            className="w-full px-3 py-2 rounded-md"
            style={{
              background: confirmedSaved ? "#4ed084" : "rgba(78,208,132,0.20)",
              border: "1px solid #4ed084",
              color: confirmedSaved ? "#08090d" : "#9198b0",
              fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 13,
              cursor: confirmedSaved ? "pointer" : "not-allowed",
            }}
            data-testid="security-2fa-codes-done-btn"
          >
            <Check size={13} className="inline mr-1" /> Done
          </button>
        </div>
      )}
    </ModalShell>
  );
};

const DisableModal = ({ onClose }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code });
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to disable 2FA.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Disable two-factor authentication" testid="security-2fa-disable-modal">
      <div className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
        Disabling 2FA will remove the extra security layer from your account. Enter your current 6-digit code to confirm.
      </div>
      <input
        type="text" inputMode="numeric" value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="••••••"
        className="w-full px-3 py-3 rounded-md outline-none"
        style={{
          background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)",
          color: "#f0f2f8", fontFamily: "'DM Mono', monospace",
          fontSize: 22, letterSpacing: "8px", textAlign: "center",
        }}
        data-testid="security-2fa-disable-input"
        autoFocus
      />
      {err && <div className="mt-3 px-3 py-2 rounded-md" style={{
        background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)",
        fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#E24B4A",
      }} data-testid="security-2fa-disable-error">{err}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-3 py-2 rounded-md" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#9198b0", fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cancel</button>
        <button onClick={submit} disabled={busy || code.length !== 6} className="px-4 py-2 rounded-md" style={{
          background: busy || code.length !== 6 ? "rgba(226,75,74,0.30)" : "#E24B4A",
          border: "1px solid #E24B4A", color: "#fff",
          fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12,
          cursor: busy || code.length !== 6 ? "not-allowed" : "pointer",
        }} data-testid="security-2fa-disable-submit">{busy ? "Disabling…" : "Disable 2FA"}</button>
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ children, onClose, title, testid }) => (
  <div
    className="fixed inset-0 flex items-center justify-center px-4"
    style={{ background: "rgba(0,0,0,0.7)", zIndex: 300 }}
    onClick={onClose}
    data-testid={testid}
  >
    <div
      className="w-full max-w-md rounded-lg p-5"
      style={{ background: "#111420", border: "1px solid rgba(155,89,182,0.30)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 16, color: "#f0f2f8" }}>{title}</h2>
        <button onClick={onClose} aria-label="Close" data-testid="modal-close" style={{
          background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
          color: "#9198b0", borderRadius: 4, padding: 4, cursor: "pointer",
        }}><X size={12} /></button>
      </div>
      {children}
    </div>
  </div>
);
